<script setup lang="ts">
import { useUserStore } from "@/domains/user/userStore";
import { useVehicleStore } from "@/domains/vehicles";
import {
  defaultDisplayedContent,
  useDashboardNavStore,
  type DashboardDialogContent,
} from "@/stores/dashboardNavStore";
import { computed, nextTick, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import CustomSolidButton from "../ui-elements/buttons/custom-buttons/CustomSolidButton.vue";
import DashboardBreadcrumbs from "./DashboardBreadcrumbs.vue";
import TooltipTab from "./TooltipTab.vue";
import AccountContent from "./account/AccountContent.vue";
import EditUserContent from "./account/EditUserContent.vue";
import FleetLocations from "./locations/FleetLocations.vue";
import LocationDetails from "./locations/LocationDetails.vue";
import MemberDetailsContent from "./my-fleet/MemberDetailsContent.vue";
import MyFleetContent from "./my-fleet/MyFleetContent.vue";
import NotificationsContent from "./notifications/NotificationsContent.vue";
import SavedTripsContent from "./trips/SavedTripsContent.vue";
import TripDetailsPage from "./trips/TripDetailsPage.vue";
import VehicleDetails from "./vehicles/VehicleDetails.vue";
import VehicleEditForm from "./vehicles/VehicleEditForm.vue";
import VehicleList from "./vehicles/VehicleList.vue";

const { mobile } = useDisplay();
const dashboardNavStore = useDashboardNavStore();
const defaultHomePage = ref(defaultDisplayedContent)
const vehicleStore = useVehicleStore();
const userStore = useUserStore();

const displayedVehicle = computed(() =>
  vehicleStore.vehicles.find(
    (v) => v.id === dashboardNavStore.displayedVehicleId,
  ),
);
const displayedVehicleIsDefault = computed(
  () =>
    !!displayedVehicle.value?.externalId &&
    displayedVehicle.value.externalId === userStore.defaultVehicleExternalId,
);

// ----------------------- dashboard visibility controls ---------------------- //

const showDashboard = computed<boolean>(() => dashboardNavStore.showDashboard);

const closeDashboard = () => {
  dashboardNavStore.showDashboard = false;
};

function keyDown(event: KeyboardEvent) {
  if (event.key === "Escape") closeDashboard();
}

// ------------------------- content display controls ------------------------- //

const tab = computed<DashboardDialogContent>(
  () => dashboardNavStore.displayedContent,
);

function viewDashboardContent(content: DashboardDialogContent) {
  dashboardNavStore.displayedContent = content;
}

function resetScrollPosition() {
  nextTick(() => {
    const myDiv = document.getElementById("dashboard-scroll-container");
    if (myDiv) myDiv.scrollTop = 0;
  });
}

const expandMyFleet = ref<number | undefined>(0);

const myFleetChildren: DashboardDialogContent[] = [
  "my-fleet",
  "fleet-member",
  "fleet-vehicles",
  "vehicle-details",
  "vehicle-edit-details",
  "fleet-locations",
  "fleet-location-details",
  "saved-trips",
];

watch(tab, (value) => {
  expandMyFleet.value = myFleetChildren.includes(value) ? 0 : undefined;
});
</script>
<template>
  <v-dialog
    :model-value="showDashboard"
    height="100%"
    @click:outside="closeDashboard"
    @keydown="keyDown($event)"
    fullscreen
    style="z-index: 1200"
  >
    <div class="modal-content">
      <div v-if="!mobile" class="side-nav-column pwt-scrollbar-styles">
        <!-- Side nav content here -->
        <v-tabs
          direction="vertical"
          hide-slider
          :mandatory="false"
          :model-value="tab"
          @change="resetScrollPosition"
        >
          <v-expansion-panels
            hide-actions
            flat
            variant="accordion"
            v-model="expandMyFleet"
          >
            <v-expansion-panel>
              <v-expansion-panel-title
                max-height="48px"
                height="48px"
                class="v-tab text-none pl-4 rounded-lg"
                :class="
                  tab === 'my-fleet' || tab === 'fleet-member' ? 'bg-primary40 text-on-primary' : 'bg-white'
                "
                @click.stop="() => viewDashboardContent('my-fleet')"
              >
                My fleet
              </v-expansion-panel-title>
              <v-expansion-panel-text class="bg-white">
                <TooltipTab
                  tab-title="Vehicles"
                  @click="viewDashboardContent('fleet-vehicles')"
                  :class="
                    tab === 'fleet-vehicles' ||
                    tab === 'vehicle-details' ||
                    tab === 'vehicle-edit-details'
                      ? 'bg-primary40 text-on-primary'
                      : 'bg-white'
                  "
                  :indent-level="1"
                />
                <TooltipTab
                  tab-title="Locations"
                  @click="() => viewDashboardContent('fleet-locations')"
                  :class="
                    tab === 'fleet-locations' ||
                    tab === 'fleet-location-details'
                      ? 'bg-primary40 text-on-primary'
                      : 'bg-white'
                  "
                  :indent-level="1"
                />
                <TooltipTab
                  tab-title="Saved trips"
                  @click="() => viewDashboardContent('saved-trips')"
                  :class="
                    tab === 'saved-trips'
                      ? 'bg-primary40 text-on-primary'
                      : 'bg-white'
                  "
                  :indent-level="1"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
          <TooltipTab
            tab-title="Notifications"
            @click="() => viewDashboardContent('notifications')"
            :class="tab === 'notifications' ? 'bg-primary40 text-on-primary' : ''"
          />
          <TooltipTab
            tab-title="Account settings"
            @click="() => viewDashboardContent('account')"
            :class="tab === 'account' ? 'bg-primary40 text-on-primary' : ''"
          />
        </v-tabs>
      </div>
      <div class="display-column" :class="{ 'display-column--mobile': mobile }">
        <div class="exit-nav-bar">
          <!-- Nav bar content here -->
          <CustomSolidButton @click="closeDashboard" > View map </CustomSolidButton>
        </div>
        <div v-if="mobile" class="sticky-nav"></div>
        <div v-if="!mobile && tab !== defaultHomePage" class="sticky-nav">
          <DashboardBreadcrumbs />
        </div>
        <div
          id="dashboard-scroll-container"
          class="pwt-scrollbar-styles"
          :class="
            tab === 'notifications'
              ? 'display-column--scroll-container--home-tab'
              : 'display-column--scroll-container'
          "
        >
          <v-tabs-window :model-value="tab" class="px-4">
            <v-tabs-window-item value="notifications">
              <NotificationsContent />
            </v-tabs-window-item>
            <v-tabs-window-item value="account">
              <AccountContent />
            </v-tabs-window-item>
            <v-tabs-window-item value="account-edit">
              <EditUserContent />
            </v-tabs-window-item>
            <v-tabs-window-item value="my-fleet">
              <MyFleetContent />
            </v-tabs-window-item>
            <v-tabs-window-item value="fleet-member">
              <MemberDetailsContent />
            </v-tabs-window-item>
            <v-tabs-window-item value="fleet-locations">
              <FleetLocations />
            </v-tabs-window-item>
            <v-tabs-window-item value="fleet-location-details">
              <LocationDetails />
            </v-tabs-window-item>
            <v-tabs-window-item value="fleet-vehicles">
              <VehicleList />
            </v-tabs-window-item>
            <v-tabs-window-item value="vehicle-details">
              <VehicleDetails
                :vehicle="displayedVehicle"
                :is-selected="displayedVehicleIsDefault"
              />
            </v-tabs-window-item>
            <v-tabs-window-item value="vehicle-edit-details">
              <VehicleEditForm :vehicle="displayedVehicle" />
            </v-tabs-window-item>
            <v-tabs-window-item value="saved-trips">
              <SavedTripsContent />
            </v-tabs-window-item>
            <v-tabs-window-item value="saved-trip-details">
              <TripDetailsPage />
            </v-tabs-window-item>
          </v-tabs-window>
        </div>
      </div>
    </div>
  </v-dialog>
</template>
<style scoped>
.pwt-scrollbar-styles {
  scrollbar-color: #ffffff #e0e0e0;
  scrollbar-width: thin;
}

.pwt-scrollbar-styles:hover {
  scrollbar-color: #eeeeee #e0e0e0;
}

.pwt-scrollbar-styles::-webkit-scrollbar {
  width: 6px;
}

.pwt-scrollbar-styles::-webkit-scrollbar-track {
  background: #ffffff;
}

.pwt-scrollbar-styles::-webkit-scrollbar-track:hover {
  background: #e0e0e0;
}

.pwt-scrollbar-styles::-webkit-scrollbar-thumb {
  background: #ffffff;
}

.pwt-scrollbar-styles::-webkit-scrollbar-thumb:hover {
  background: #eeeeee;
}

.modal-content {
  background-color: #ffffff;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.side-nav-column {
  width: 20%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  border-right: 2px solid rgb(var(--v-theme-primary20));
  background-color: #ffffff;
  scroll-behavior: smooth;
}

.display-column {
  width: 80%;
  height: 100%;
}

.display-column--mobile {
  width: 100%;
}

.display-column--scroll-container {
  height: calc(100% - 130px);
  overflow-y: auto;
  scroll-behavior: smooth;
}

.display-column--scroll-container--home-tab {
  height: calc(100% - 60px);
  overflow-y: auto;
  scroll-behavior: smooth;
}

.exit-nav-bar {
  padding: 20px;
  display: flex;
  flex-direction: row;
  justify-content: end;
  height: 80px;
}

.sticky-nav {
  position: sticky;
  top: 0;
  z-index: 1;
  height: 70px;
}

* :deep(.v-expansion-panel-title--active) {
  min-height: 48px !important;
  height: 48px !important;
  max-height: 48px !important;
}

* :deep(.v-expansion-panel-text__wrapper) {
  padding: 0;
}
</style>
